import React from 'react';
import {
  AppButton,
  Box,
  FlexControlledForm,
  Preloader,
  ProcessingRuleItem,
  Text,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { Modal } from '@/components';
import { WarnModal } from '@/components/elements';

import useAddTriggerState from './hooks/useAddTriggerState';
import useProcessingRuleModal from './hooks/useProcessingRuleModal';
import { AGGREGATE_TRIGGER_KEY, EMBEDDED_TRIGGER_KEY } from './hooks/useTierFields';
import { TierField } from './hooks/useTiers';
import AddAnotherTier from './AddAnotherTier';
import AddTriggerModal from './AddTriggerModal';
import TierItem from './TierItem';

interface Props {
  updatedElement?: ProcessingRuleItem;
  visible?: boolean;
  onToggleVision?: (value: boolean) => void;
  onUpdateElement?: (ruleId?: string) => void;
  isTemplateMode?: boolean;
}

const ProcessingRuleModalV2: React.FC<Props> = ({
  updatedElement,
  visible,
  onToggleVision,
  onUpdateElement,
  isTemplateMode,
}) => {
  const {
    state,
    setState,
    fields,
    setShouldConfirmOnClose,
    tiersValues,
    getFieldValuesById,
    handleChangeValues,
    handleRemoveById,
    handleAddNewTier,
    onCloseModalHandler,
    handleSubmit,
    isSubmitted,
    isLoading,
    isServerError,
    setServerError,
  } = useProcessingRuleModal({ updatedElement, visible, onToggleVision, onUpdateElement });

  const {
    addTriggerVisible,
    addTriggerType,
    handleOpenAddTrigger,
    handleTriggerCreated,
    handleCloseAddTrigger,
  } = useAddTriggerState({ getFieldValuesById, handleChangeValues });

  return (
    <>
      <Modal visible={!!visible && !addTriggerVisible} onSetVisible={() => onCloseModalHandler(true)}>
        <Box direction="column">
          <Box direction="row" align="center" justify="center" pad={{ bottom: 'spacing24' }}>
            <Text size="2xl" color="textTitle" weight="bold">
              {updatedElement ? 'Customize Processing Rule' : 'Create a Processing Rule'}
            </Text>
          </Box>
          <Box>
            <Box pad="l" background="module" round="moduleRound">
              <Box
                background="canvas"
                border={{ size: 'small', color: 'border2' }}
                pad={{ vertical: 'l' }}
                elevation="default"
                margin={{ bottom: 'l' }}
                round="container1Round"
              >
                <FlexControlledForm
                  isModalType
                  fields={fields}
                  editMode
                  onDirty={setShouldConfirmOnClose}
                  onChangeValues={setState}
                  showError={isSubmitted}
                  wrapperStyle={{ border: 'none' }}
                  formStyle={{ marginBottom: 0, marginTop: 0 }}
                />
              </Box>
              {tiersValues.map((item: TierField, index: number) => (
                <TierItem
                  id={item.id}
                  isRemoveMode={tiersValues.length > 1}
                  index={index}
                  isErrorShowed={isSubmitted}
                  isLastItem={_.last(tiersValues) === item}
                  onRemove={handleRemoveById}
                  key={item.id}
                  defaultValues={getFieldValuesById(item.id)}
                  onChangeValues={handleChangeValues(item.id)}
                  tiersValues={tiersValues}
                  setShouldConfirmOnClose={setShouldConfirmOnClose}
                  processingRuleType={state.processingRuleType}
                  isHealthPlanProcessingRule={!!updatedElement?.isHealthPlanProcessingRule}
                  isTemplateMode={isTemplateMode}
                  onAddNewAggregateTrigger={() => handleOpenAddTrigger(item.id || '', AGGREGATE_TRIGGER_KEY)}
                  onAddNewEmbeddedTrigger={() => handleOpenAddTrigger(item.id || '', EMBEDDED_TRIGGER_KEY)}
                />
              ))}
              <AddAnotherTier onClick={handleAddNewTier} />
            </Box>
            <Box direction="row" align="center" justify="end" pad={{ top: 'l' }}>
              <AppButton buttonType="secondary" width="control" onClick={() => onCloseModalHandler(true)}>
                Cancel
              </AppButton>
              <Box margin={{ left: 's' }}>
                <AppButton width="control" onClick={handleSubmit} disabled={isLoading}>
                  {isLoading ? (
                    <Preloader color="white" />
                  ) : (
                    <Text>{updatedElement ? 'Save' : 'Create'}</Text>
                  )}
                </AppButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
      <WarnModal
        visible={isServerError}
        onSetVisible={() => {
          setServerError(false);
        }}
        header={SERVER_ERROR_MODAL_TITLE}
        helptext={SERVER_ERROR_MODAL_TEXT}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try Again"
      />
      <AddTriggerModal
        visible={addTriggerVisible}
        defaultTriggerType={addTriggerType}
        onClose={handleCloseAddTrigger}
        onCreated={handleTriggerCreated}
      />
    </>
  );
};

export default ProcessingRuleModalV2;
